import React, { useState, memo, useMemo, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { CloseButton } from '@/components/ui/close-button';
import { MonthPicker } from '@/components/ui/month-picker';
import { CalendarDays, ArrowLeft, ArrowRight, CalendarIcon, EyeIcon, EyeOffIcon, Columns, Settings, X, RotateCcw, AlertTriangle, MoreVertical, Share2, GraduationCap } from 'lucide-react';
import { format, setMonth, setYear, getYear, getMonth } from 'date-fns';
import { CalendarScrollMode } from './context/ShiftUIProvider';
import CalendarTutorialDialog from './CalendarTutorialDialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { ButtonGroup } from '@/components/ui/button-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Tables } from '@/integrations/supabase/types';
import { DayShift, Employee } from '@/types';
import ShareCalendarDialog from './ShareCalendarDialog';
import { TimeRangeSelector, TimeRangeValue } from '@/components/ui/time-range-selector';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useDimming } from '@/hooks/ui/useDimming';
import { SHIFT_STATUS_CONFIG, ShiftStatus } from '@/lib/scheduling/shift-status-types';
import { ShiftStatusService } from '@/lib/scheduling/shift-status-service';
import { useShiftStatusService } from '@/hooks/scheduling/useShiftStatusService';
import { useBusinessCreationValidation } from '@/lib/business';
import { Users, Filter } from 'lucide-react';
import { useHistoricalEdit } from '@/context/HistoricalEditContext';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { getTimezoneIndicator, getTimezoneFullName, isTimezoneMismatch } from '@/lib/core/timezone-utils';
import { Globe } from 'lucide-react';

// Utility functions for calculating shift counts
interface ShiftCounts {
  total: number;
  unassigned: number;
  employeeCounts: Record<string, number>;
  statusCounts: Record<string, number>;
}

const calculateShiftCounts = (shifts: DayShift[] = [], employees: Employee[] = [], shiftStatusService: ShiftStatusService): ShiftCounts => {
  const counts: ShiftCounts = {
    total: shifts.length,
    unassigned: 0,
    employeeCounts: {},
    statusCounts: {}
  };

  // Initialize employee counts
  employees.forEach(employee => {
    counts.employeeCounts[employee.id] = 0;
  });

  // Initialize status counts
  Object.keys(SHIFT_STATUS_CONFIG).forEach(status => {
    counts.statusCounts[status] = 0;
  });

  shifts.forEach(shift => {
    // Count employee assignments
    if (!shift.employeeId) {
      counts.unassigned++;
    } else {
      counts.employeeCounts[shift.employeeId] = (counts.employeeCounts[shift.employeeId] || 0) + 1;
    }

    // Count by status using timezone-aware service
    const calculatedStatus = shiftStatusService.calculateShiftStatus(
      shift.startTime,
      shift.endTime,
      shift.employeeId,
      shift.actualArrivalTime || null,
      shift.actualDepartureTime || null
    );

    // Use the calculated status string
    const statusKey = calculatedStatus.toString();
    if (Object.prototype.hasOwnProperty.call(counts.statusCounts, statusKey)) {
      counts.statusCounts[statusKey]++;
    }
  });

  return counts;
};

// Calculate how many shifts will be highlighted with current filters
const calculateHighlightedShifts = (shifts: DayShift[] = [], employees: Employee[] = [], selectedEmployees: string[], selectedStatuses: string[], shiftStatusService: ShiftStatusService): number => {
  if (selectedEmployees.length === 0 && selectedStatuses.length === 0) {
    // No filters active, all shifts are highlighted
    return shifts.length;
  }

  return shifts.filter(shift => {
    // Check employee filter (no longer includes "unassigned" option)
    let passesEmployeeFilter = true;
    if (selectedEmployees.length > 0) {
      if (shift.employeeId && selectedEmployees.includes(shift.employeeId)) {
        passesEmployeeFilter = true;
      } else {
        passesEmployeeFilter = false;
      }
    }

    // Check status filter using timezone-aware service
    let passesStatusFilter = true;
    if (selectedStatuses.length > 0) {
      const calculatedStatus = shiftStatusService.calculateShiftStatus(
        shift.startTime,
        shift.endTime,
        shift.employeeId,
        shift.actualArrivalTime || null,
        shift.actualDepartureTime || null
      );
      const statusKey = calculatedStatus.toString();
      passesStatusFilter = selectedStatuses.includes(statusKey);
    }

    // Both filters must pass (AND logic)
    return passesEmployeeFilter && passesStatusFilter;
  }).length;
};

type Event = Tables<'events'>;
type EventAssignment = Tables<'event_assignments'>;

interface EventWithAssignments extends Event {
  event_assignments: (EventAssignment & {
    employees: {
      name: string;
      email: string;
    } | null;
  })[];
}

interface CalendarHeaderProps {
  currentDisplayMonth: Date;
  displayOnlyDay?: Date | null;
  hidePastDays: boolean;
  events?: EventWithAssignments[];
  showEvents?: boolean;
  onToggleEvents?: () => void;
  hideControls?: boolean; // New prop to hide navigation and toggle controls
  // New props from ShiftControls
  onPreviousMonth?: () => void;
  onNextMonth?: () => void;
  onSetToday?: () => void;
  onToggleHidePastDays?: (checked: boolean) => void;
  onSyncCalendar?: () => void;
  businessName?: string;
  calendarRef?: React.RefObject<HTMLElement>;
  shifts?: DayShift[];
  employees?: Employee[];
  onMonthSelect?: (date: Date) => void;
  businessCreatedAt?: string;
  isActionsPanelCollapsed?: boolean;
  onToggleActionsPanel?: () => void;
  isEditingMode?: boolean;
  // Business opening hours for calculating defaults
  openingHours?: any[];
  // Time range control props
  show24Hours?: boolean;
  onToggleShow24Hours?: (checked: boolean) => void;
  // Simple display hours props (only for shift management variant)
  smartDisplayHours?: {
    range: { start: number; end: number; startTime: string; endTime: string };
    calculationMethod: 'user-override' | 'opening-hours' | 'fallback';
    canExpand: boolean;
    expandRange: (newRange: Partial<{ start: number; end: number; startTime: string; endTime: string }>) => void;
    resetToAuto: () => void;
    isLoading: boolean;
  };
  // Calendar variant prop
  variant?: 'full' | 'insights' | 'timetracking';
  // Tutorial callback
  onStartInteractiveTutorial?: () => void;
  // Control ref for tutorial integration
  controlRef?: React.MutableRefObject<{
    openFilter: () => void;
    closeFilter: () => void;
    openSettings: () => void;
    closeSettings: () => void;
  } | null>;
  // Business timezone for indicator display
  businessTimezone?: string;
  // Infinite scroll mode props
  /** Calendar scroll mode - defaults to 'monthly' */
  scrollMode?: CalendarScrollMode;
}

const CalendarHeader: React.FC<CalendarHeaderProps> = ({
  currentDisplayMonth,
  displayOnlyDay,
  hidePastDays,
  events,
  showEvents,
  onToggleEvents,
  hideControls = false,
  onPreviousMonth,
  onNextMonth,
  onSetToday,
  onToggleHidePastDays,
  onSyncCalendar,
  businessName,
  calendarRef,
  shifts,
  employees,
  onMonthSelect,
  businessCreatedAt,
  isActionsPanelCollapsed,
  onToggleActionsPanel,
  isEditingMode,
  // Business opening hours for calculating defaults
  openingHours,
  // Time range control props
  show24Hours,
  onToggleShow24Hours,
  // Simple display hours props
  smartDisplayHours,
  // Calendar variant prop
  variant = 'full',
  // Tutorial callback
  onStartInteractiveTutorial,
  // Control ref
  controlRef,
  // Business timezone for indicator
  businessTimezone,
  // Infinite scroll mode
  scrollMode = 'monthly'
}) => {
  const [isSettingsPopoverOpen, setIsSettingsPopoverOpen] = useState(false);
  const [isUnifiedFilterOpen, setIsUnifiedFilterOpen] = useState(false);
  // Remove tab state since we're showing both sections together
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);

  // Timezone-aware shift status service
  const shiftStatusService = useShiftStatusService();

  // Expose control methods for tutorial
  React.useEffect(() => {
    if (controlRef) {
      controlRef.current = {
        openFilter: () => setIsUnifiedFilterOpen(true),
        closeFilter: () => setIsUnifiedFilterOpen(false),
        openSettings: () => setIsSettingsPopoverOpen(true),
        closeSettings: () => setIsSettingsPopoverOpen(false),
      };
    }
  }, [controlRef]);

  // Integrate with dimming context for select dimming effects and filtering
  const {
    handleSelectOpen,
    handleSelectClose,
    selectedEmployeeFilter,
    selectedStatusFilter,
    setEmployeeFilter,
    setStatusFilter,
    showAllEmployees,
    showUnassignedShifts,
    showEmployeeShifts,
    showAllStatuses,
    showStatusShifts,
    clearEmployeeFilter,
    clearStatusFilter,
    isDimmingActive
  } = useDimming();

  // Synchronize local filter state with dimming context
  useEffect(() => {
    // Pass array-based employee selections directly to enhanced dimming context
    if (selectedEmployees.length === 0) {
      // No employees selected - clear filter
      setEmployeeFilter(null);
    } else {
      // Pass the full array for multi-select support
      setEmployeeFilter(selectedEmployees);
    }
  }, [selectedEmployees, setEmployeeFilter]);

  useEffect(() => {
    // Pass array-based status selections directly to enhanced dimming context
    if (selectedStatuses.length === 0) {
      // No statuses selected - clear filter
      setStatusFilter(null);
    } else {
      // Pass the full array for multi-select support
      setStatusFilter(selectedStatuses);
    }
  }, [selectedStatuses, setStatusFilter]);

  // Calculate shift counts for filter options
  const shiftCounts = useMemo(() => {
    return calculateShiftCounts(shifts, employees, shiftStatusService);
  }, [shifts, employees, shiftStatusService]);

  // Calculate how many shifts will be highlighted with current filters
  const highlightedShiftsCount = useMemo(() => {
    return calculateHighlightedShifts(shifts, employees, selectedEmployees, selectedStatuses, shiftStatusService);
  }, [shifts, employees, selectedEmployees, selectedStatuses, shiftStatusService]);

  // Helper functions for multi-select (Safari-compatible)
  const toggleEmployeeSelection = useCallback((employeeId: string) => {
    setSelectedEmployees(prev => {
      if (prev.includes(employeeId)) {
        return prev.filter(id => id !== employeeId);
      } else {
        return [...prev, employeeId];
      }
    });
  }, []);

  const toggleStatusSelection = useCallback((status: string) => {
    setSelectedStatuses(prev => {
      if (prev.includes(status)) {
        return prev.filter(s => s !== status);
      } else {
        return [...prev, status];
      }
    });
  }, []);

  const clearAllEmployeeFilters = useCallback(() => {
    setSelectedEmployees([]);
    // The useEffect will handle clearing the dimming context
  }, []);

  const clearAllStatusFilters = useCallback(() => {
    setSelectedStatuses([]);
    // The useEffect will handle clearing the dimming context
  }, []);

  const selectAllEmployees = useCallback(() => {
    const allIds = (employees || []).map(e => e.id);
    setSelectedEmployees(allIds);
  }, [employees]);

  const selectAllStatuses = useCallback(() => {
    const allStatuses = Object.keys(SHIFT_STATUS_CONFIG);
    setSelectedStatuses(allStatuses);
  }, []);

  // Historical edit context for protecting past shifts
  const {
    isHistoricalEditEnabled,
    enableHistoricalEdit,
    disableHistoricalEdit
  } = useHistoricalEdit();

  // Business creation validation for month navigation
  const { isMonthNavigationDisabled } = useBusinessCreationValidation(businessCreatedAt);
  return (
    <div className="text-sm font-medium flex items-center justify-between">
      {variant === 'timetracking' || variant === 'insights' ? (
        <>
          <div>
            <span>Shift Schedule</span>
          </div>
          <div className="flex-1 text-right">
            <span className="text-sm font-medium">
              {displayOnlyDay ? format(displayOnlyDay, 'EEEE, MMMM d, yyyy') : format(currentDisplayMonth, 'MMMM yyyy')}
            </span>
          </div>
        </>
      ) : (
        <div className="flex items-center gap-2">
          <span>
            {displayOnlyDay
              ? `Shift Schedule - ${format(displayOnlyDay, 'EEEE, MMMM d, yyyy')}`
              : scrollMode === 'infinite'
                ? 'Shift Schedule'
                : `Shift Schedule - ${format(currentDisplayMonth, 'MMMM yyyy')}`
            }
          </span>
          {/* Timezone indicator - shows when business timezone differs from browser */}
          {businessTimezone && isTimezoneMismatch(businessTimezone) && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 rounded-md cursor-help">
                  <Globe className="h-3 w-3" />
                  {getTimezoneIndicator(businessTimezone)}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>All times shown in {getTimezoneFullName(businessTimezone)}</p>
                <p className="text-xs text-muted-foreground">Your browser is in a different timezone</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      )}
      
      {/* Controls */}
      <div className="flex items-center flex-wrap" style={{gap: '0.5rem'}}>
        {!hideControls && (
          <>
            {/* Navigation Group - Filter + View Toggle + Navigation + Settings */}
            <div
              className="inline-flex items-center gap-1"
              data-tutorial="navigation-group"
            >
              {/* Filter Button with Clear */}
              <div className="inline-flex items-center">
                <div className="relative">
                  {(selectedEmployees.length + selectedStatuses.length) > 0 && (
                    <span className="absolute -top-1 -right-1 z-10 h-4 min-w-4 px-0.5 bg-primary text-primary-foreground text-[10px] rounded-full flex items-center justify-center font-medium pointer-events-none">
                      {selectedEmployees.length + selectedStatuses.length}
                    </span>
                  )}
                  <Popover open={isUnifiedFilterOpen} onOpenChange={setIsUnifiedFilterOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        className={`h-8 w-8 ${
                          selectedEmployees.length > 0 || selectedStatuses.length > 0
                            ? 'text-primary'
                            : ''
                        }`}
                        aria-label="Filter shifts"
                        data-tutorial="filter-dropdown"
                      >
                        <Filter className="h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <div className="flex flex-col">
                      {/* Two-column layout for filters */}
                      <div className="grid grid-cols-2 gap-0">
                        {/* Employee Filter Column */}
                        <div className="p-3 border-r">
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <h4 className="font-medium text-sm flex items-center gap-2">
                                <Users className="h-4 w-4" />
                                Employees
                                {selectedEmployees.length > 0 && (
                                  <span className="bg-indigo-100 text-indigo-800 text-xs px-1.5 py-0.5 rounded-full">
                                    {selectedEmployees.length}
                                  </span>
                                )}
                              </h4>
                            </div>
                            <div className="flex justify-center" style={{gap: '0.25rem'}}>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-xs flex-1"
                                onClick={selectAllEmployees}
                              >
                                All
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-xs flex-1"
                                onClick={clearAllEmployeeFilters}
                              >
                                None
                              </Button>
                            </div>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                              {(employees || []).map((employee) => (
                                <div key={employee.id} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={employee.id}
                                    checked={selectedEmployees.includes(employee.id)}
                                    onCheckedChange={() => toggleEmployeeSelection(employee.id)}
                                  />
                                  <label
                                    htmlFor={employee.id}
                                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2 cursor-pointer flex-1"
                                  >
                                    <div className="w-2 h-2 bg-green-500 rounded-full" aria-hidden="true"></div>
                                    <span className="truncate">{employee.name} ({shiftCounts.employeeCounts[employee.id] || 0})</span>
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Status Filter Column */}
                        <div className="p-3">
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <h4 className="font-medium text-sm flex items-center gap-2">
                                <Filter className="h-4 w-4" />
                                Status
                                {selectedStatuses.length > 0 && (
                                  <span className="bg-indigo-100 text-indigo-800 text-xs px-1.5 py-0.5 rounded-full">
                                    {selectedStatuses.length}
                                  </span>
                                )}
                              </h4>
                            </div>
                            <div className="flex justify-center" style={{gap: '0.25rem'}}>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-xs flex-1"
                                onClick={selectAllStatuses}
                              >
                                All
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-xs flex-1"
                                onClick={clearAllStatusFilters}
                              >
                                None
                              </Button>
                            </div>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                              {Object.entries(SHIFT_STATUS_CONFIG)
                                .sort(([,a], [,b]) => a.priority - b.priority)
                                .map(([status, config]) => {
                                  const StatusIcon = config.icon;
                                  const count = shiftCounts.statusCounts[status] || 0;

                                  return (
                                    <div key={status} className="flex items-center space-x-2">
                                      <Checkbox
                                        id={status}
                                        checked={selectedStatuses.includes(status)}
                                        onCheckedChange={() => toggleStatusSelection(status)}
                                      />
                                      <label
                                        htmlFor={status}
                                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2 cursor-pointer flex-1"
                                      >
                                        <div className={`p-0.5 rounded-sm ${config.badgeColor.background}`}>
                                          <StatusIcon className={`h-3 w-3 ${config.badgeColor.text}`} aria-hidden="true" />
                                        </div>
                                        <span className="truncate">{config.label} ({count})</span>
                                      </label>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Highlighting counter at bottom */}
                      <div className="border-t px-3 py-2">
                        <div className="flex items-center justify-between text-sm">
                          <span>Highlighting:</span>
                          <span className="font-medium">
                            {highlightedShiftsCount} shift{highlightedShiftsCount !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                  </PopoverContent>
                  </Popover>
                </div>
                {/* Clear Filters X Button */}
                {(selectedEmployees.length > 0 || selectedStatuses.length > 0) && (
                  <CloseButton
                    variant="destructive"
                    size="lg"
                    onClick={() => {
                      clearAllEmployeeFilters();
                      clearAllStatusFilters();
                    }}
                    srText="Clear all filters"
                  />
                )}
              </div>

              {/* Navigation: Monthly mode vs Infinite scroll mode */}
              {scrollMode === 'infinite' ? (
                <>
                  {/* Today button - for infinite scroll mode */}
                  {onSetToday && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-3"
                      onClick={onSetToday}
                    >
                      Today
                    </Button>
                  )}
                </>
              ) : (
                <>
                  {/* Previous button */}
                  {onPreviousMonth && (
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={onPreviousMonth}
                      disabled={isMonthNavigationDisabled(currentDisplayMonth.getMonth(), currentDisplayMonth.getFullYear(), 'previous')}
                      data-tutorial="month-nav"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      <span className="sr-only">Previous Month</span>
                    </Button>
                  )}

                  {/* Month Picker */}
                  {onMonthSelect && (
                    <MonthPicker
                      value={currentDisplayMonth}
                      onChange={onMonthSelect}
                      businessCreationDate={businessCreatedAt}
                      onTodayClick={onSetToday}
                      showTodayButton={!!onSetToday}
                      dataTutorial="month-nav"
                      size="sm"
                      className="h-8"
                    />
                  )}

                  {/* Next button */}
                  {onNextMonth && (
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={onNextMonth}
                      data-tutorial="month-nav"
                    >
                      <ArrowRight className="h-4 w-4" />
                      <span className="sr-only">Next Month</span>
                    </Button>
                  )}
                </>
              )}

              {/* Settings Button with View Settings Popover */}
              <Popover open={isSettingsPopoverOpen} onOpenChange={setIsSettingsPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    data-tutorial="settings-button"
                  >
                    <Settings className="h-4 w-4" />
                    <span className="sr-only">View settings</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-3" align="end">
                  <div className="space-y-3">
                    <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">View Settings</div>

                    {/* Hide Past Days */}
                    <div className="flex items-center justify-between">
                      <Label htmlFor="hide-past-settings" className="flex items-center gap-2 text-sm cursor-pointer">
                        <EyeOffIcon className="h-4 w-4" />
                        Hide Past Days
                      </Label>
                      <Switch
                        id="hide-past-settings"
                        checked={hidePastDays}
                        onCheckedChange={onToggleHidePastDays}
                      />
                    </div>

                    {/* Display Hours & Time Format - Only for full variant with smartDisplayHours */}
                    {smartDisplayHours && variant === 'full' && (
                      <div className="border-t pt-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Display Hours</span>
                          {smartDisplayHours.calculationMethod === 'user-override' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={smartDisplayHours.resetToAuto}
                              className="h-5 px-1.5 text-[10px]"
                            >
                              <RotateCcw className="h-3 w-3 mr-0.5" />
                              Reset
                            </Button>
                          )}
                        </div>
                        <TimeRangeSelector
                          value={{
                            startTime: smartDisplayHours.range.startTime,
                            endTime: smartDisplayHours.range.endTime
                          }}
                          onChange={(newRange: TimeRangeValue) => {
                            const startHour = parseInt(newRange.startTime.split(':')[0]);
                            const endHour = parseInt(newRange.endTime.split(':')[0]);
                            smartDisplayHours.expandRange({
                              start: startHour,
                              end: endHour,
                              startTime: newRange.startTime,
                              endTime: newRange.endTime
                            });
                          }}
                          showFormatToggle={true}
                          onFormatToggle={(show24Hours) => {
                            if (onToggleShow24Hours) {
                              onToggleShow24Hours(show24Hours);
                            }
                          }}
                          minHour={0}
                          maxHour={36}
                          intervalMinutes={60}
                          className="w-full"
                        />
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* Editing Group - Edit Mode, Show Panel, Edit Past Shifts */}
            {variant === 'full' && (
              <div
                className="inline-flex items-center gap-1"
                data-tutorial="editing-group"
              >
                {/* View / Edit Toggle */}
                {onToggleActionsPanel && (
                  <ButtonGroup aria-label="Editing mode">
                    <Button
                      variant={!isEditingMode ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => isEditingMode && onToggleActionsPanel()}
                      className="h-8 px-3 text-xs"
                    >
                      View
                    </Button>
                    <Button
                      variant={isEditingMode ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => !isEditingMode && onToggleActionsPanel()}
                      className="h-8 px-3 text-xs"
                    >
                      Edit
                    </Button>
                  </ButtonGroup>
                )}

                {/* Edit Past Shifts Button - only visible in Edit mode */}
                {isEditingMode && (
                  <Button
                    variant="outline"
                    size="sm"
                    data-active={isHistoricalEditEnabled}
                    onClick={() => isHistoricalEditEnabled ? disableHistoricalEdit() : enableHistoricalEdit()}
                    className="h-8 px-3 text-xs"
                  >
                    Edit Past Shifts
                  </Button>
                )}
              </div>
            )}

            {/* More Menu - Share & Tutorial */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  data-tutorial="more-menu"
                >
                  <MoreVertical className="h-4 w-4" />
                  <span className="sr-only">More options</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {/* Share Calendar - Trigger dialog */}
                <ShareCalendarDialog
                  currentDisplayMonth={currentDisplayMonth}
                  businessName={businessName}
                  calendarRef={calendarRef}
                  onSyncCalendar={onSyncCalendar}
                  shifts={shifts}
                  employees={employees}
                  trigger={
                    <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                      <Share2 className="mr-2 h-4 w-4" />
                      Share Calendar
                    </DropdownMenuItem>
                  }
                />

                {/* Tutorial - Trigger dialog */}
                <CalendarTutorialDialog
                  onStartInteractiveTutorial={onStartInteractiveTutorial}
                  trigger={
                    <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                      <GraduationCap className="mr-2 h-4 w-4" />
                      Tutorial
                    </DropdownMenuItem>
                  }
                />

                {/* Events Toggle */}
                {events && events.length > 0 && onToggleEvents && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={onToggleEvents}>
                      <CalendarDays className="mr-2 h-4 w-4" />
                      {showEvents ? 'Hide' : 'Show'} Events ({events.length})
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

          </>
        )}
      </div>
    </div>
  );
};

export default CalendarHeader;
